import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Send, Smile, AlertTriangle, Clock, X, FileText } from 'lucide-react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import WhatsAppTemplateSendModal from './WhatsAppTemplateSendModal';
import { getErrorMessage } from '../../lib/utils';


interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  sender_type: string;
  created_at: string;
  status?: string;
}

interface Conversation {
  id: string;
  channel: string;
  status: string;
  contact_number: string;
  contact_name: string;
  last_inbound_at: string | null;
  last_message_at: string | null;
}

interface WindowStatus {
  window_active: boolean;
  requires_template: boolean;
  last_inbound_at: string | null;
  expires_at: string | null;
}

interface WhatsAppSender {
  id: string;
  phone_number: string;
  friendly_name?: string;
}

interface WhatsAppTemplate {
  id: string;
  friendly_name: string;
  body_text: string;
  approval_status: string;
  twilio_content_sid?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadPhone: string;
  leadName: string;
}

function getDateLabel(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDay.getTime() === today.getTime()) return 'Oggi';
  if (msgDay.getTime() === yesterday.getTime()) return 'Ieri';

  const diffDays = Math.floor((today.getTime() - msgDay.getTime()) / 86400000);
  if (diffDays < 7) {
    return date.toLocaleDateString('it-IT', { weekday: 'long' });
  }
  const year = date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined;
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function getWindowCountdown(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'scaduta';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `scade tra ${h}h ${m}m`;
  return `scade tra ${m}m`;
}

export default function WhatsAppMiniChatModal({ open, onClose, leadId, leadPhone, leadName }: Props) {
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [windowStatus, setWindowStatus] = useState<WindowStatus | null>(null);
  const [senders, setSenders] = useState<WhatsAppSender[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);

  const [text, setText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedSender, setSelectedSender] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [useTemplateMode, setUseTemplateMode] = useState(false);
  const [showTemplateShortcut, setShowTemplateShortcut] = useState(false);

  const selectedTemplateObj = templates.find(t => t.id === selectedTemplate);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const loadMessages = useCallback(async (convId: string) => {
    try {
      const res = await apiClient.get<{ messages?: Message[]; data?: { messages?: Message[] } }>(`/messaging/conversations/${convId}/messages?limit=100`);
      const payload = res.data;
      setMessages(payload?.data?.messages || payload?.messages || []);
      scrollToBottom();
    } catch {
      // silently ignore refresh errors
    }
  }, [scrollToBottom]);

  const loadWindowStatus = useCallback(async (convId: string) => {
    try {
      const res = await apiClient.get<{ window_active: boolean; requires_template: boolean; last_inbound_at: string | null; expires_at: string | null }>(`/messaging/conversations/${convId}/window-status`);
      setWindowStatus(res.data ?? null);
    } catch {
      setWindowStatus({ window_active: false, requires_template: true, last_inbound_at: null, expires_at: null });
    }
  }, []);

  const loadStaticData = useCallback(async () => {
    try {
      const [sendersRes, templatesRes] = await Promise.all([
        apiClient.get<{ senders?: WhatsAppSender[]; data?: { senders?: WhatsAppSender[] } }>('/messaging/senders?channel=whatsapp'),
        apiClient.get<{ templates?: WhatsAppTemplate[]; data?: { templates?: WhatsAppTemplate[] } }>('/messaging/templates?status=approved&limit=100'),
      ]);
      const sPayload = sendersRes.data;
      const tPayload = templatesRes.data;
      const s: WhatsAppSender[] = sPayload?.data?.senders || sPayload?.senders || [];
      const t: WhatsAppTemplate[] = tPayload?.data?.templates || tPayload?.templates || [];
      setSenders(s);
      setTemplates(t);
      if (s.length > 0) setSelectedSender(s[0]!.phone_number! || s[0]!.id!);
    } catch {
      // ignore
    }
  }, []);

  const init = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setText('');
    setSelectedTemplate('');
    setUseTemplateMode(false);
    setShowEmojiPicker(false);
    setMessages([]);
    setConversation(null);
    setWindowStatus(null);

    try {
      await loadStaticData();
      const res = await apiClient.get<{ conversations?: Conversation[]; data?: { conversations?: Conversation[] } }>(
        `/messaging/conversations?lead_id=${leadId}&channel=whatsapp&limit=1`
      );
      const convPayload = res.data;
      const conv = convPayload?.data?.conversations?.[0] || convPayload?.conversations?.[0] || null;
      setConversation(conv);
      if (conv) {
        await Promise.all([loadMessages(conv.id), loadWindowStatus(conv.id)]);
      } else {
        setWindowStatus({ window_active: false, requires_template: true, last_inbound_at: null, expires_at: null });
      }
    } catch {
      setWindowStatus({ window_active: false, requires_template: true, last_inbound_at: null, expires_at: null });
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [open, leadId, loadStaticData, loadMessages, loadWindowStatus, scrollToBottom]);

  useEffect(() => {
    if (open) {
      init();
    } else {
      if (refreshRef.current) clearInterval(refreshRef.current);
    }
  }, [open, init]);

  useEffect(() => {
    if (!open || !conversation) return;
    refreshRef.current = setInterval(() => {
      loadMessages(conversation.id);
      loadWindowStatus(conversation.id);
    }, 12000);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [open, conversation, loadMessages, loadWindowStatus]);

  const handleEmojiSelect = (emoji: { native: string }) => {
    setText(prev => prev + emoji.native);
    setShowEmojiPicker(false);
  };

  const handleSendText = async () => {
    if (!text.trim() || !conversation) return;
    setSending(true);
    try {
      await apiClient.post(`/messaging/conversations/${conversation.id}/messages`, { content: text.trim() });
      setText('');
      await loadMessages(conversation.id);
      toast({ title: 'Messaggio inviato' });
    } catch (e) {
      toast({ title: 'Errore invio', description: getErrorMessage(e), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!selectedSender || !selectedTemplate) {
      toast({ title: 'Seleziona mittente e template', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await apiClient.post('/messaging/conversations', {
        contact_phone: leadPhone,
        contact_name: leadName,
        channel: 'whatsapp',
        from_number: selectedSender,
        template_id: selectedTemplate,
        lead_id: leadId,
      });
      toast({ title: 'Template inviato', description: `Messaggio inviato a ${leadName}` });
      setSelectedTemplate('');
      await init();
    } catch (e) {
      toast({ title: 'Errore invio template', description: getErrorMessage(e), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const windowOpen = windowStatus?.window_active === true && !useTemplateMode;
  const showTemplateZone = !windowOpen;

  // Group messages by date for separators
  const groupedMessages: Array<{ dateLabel: string; messages: Message[] }> = [];
  for (const msg of messages) {
    const label = getDateLabel(msg.created_at);
    const last = groupedMessages[groupedMessages.length - 1];
    if (!last || last.dateLabel !== label) {
      groupedMessages.push({ dateLabel: label, messages: [msg] });
    } else {
      last.messages.push(msg);
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop — non chiude il modal al click */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal panel */}
      <div
        className="relative z-10 bg-card rounded-xl shadow-2xl flex flex-col w-full max-w-lg mx-4"
        style={{ height: '75vh', maxHeight: '700px' }}
      >
        {/* HEADER */}
        <div className="px-4 pt-4 pb-3 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground leading-tight">{leadName}</p>
                <p className="text-xs text-muted-foreground">{leadPhone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {windowStatus && (
                windowStatus.window_active ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200 text-xs font-normal">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1 inline-block" />
                    {windowStatus.expires_at ? getWindowCountdown(windowStatus.expires_at) : 'Finestra aperta'}
                  </Badge>
                ) : (
                  <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs font-normal">
                    <Clock className="w-3 h-3 mr-1" />
                    Fuori finestra 24h
                  </Badge>
                )
              )}
              <button
                type="button"
                onClick={onClose}
                className="ml-1 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Chiudi"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* BODY CHAT */}
        <div className="flex-1 overflow-y-auto px-4 py-3 bg-muted" style={{ minHeight: 0 }}>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                  <div className={`h-10 rounded-2xl bg-muted-foreground/20 animate-pulse ${i % 2 === 0 ? 'w-40' : 'w-48'}`} />
                </div>
              ))}
            </div>
          ) : groupedMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <MessageSquare className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">Nessun messaggio precedente</p>
              <p className="text-xs mt-1">Inizia la conversazione qui sotto</p>
            </div>
          ) : (
            <div className="space-y-1">
              {groupedMessages.map((group) => (
                <div key={group.dateLabel}>
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-medium">
                      {group.dateLabel}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  {group.messages.map((msg) => {
                    const isInbound = msg.direction === 'inbound';
                    return (
                      <div key={msg.id} className={`flex mb-1 ${isInbound ? 'justify-start' : 'justify-end'}`}>
                        <div
                          className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm relative shadow-sm ${
                            isInbound
                              ? 'bg-green-50 border border-green-100 text-gray-800 rounded-tl-sm'
                              : 'bg-card border border-border text-foreground rounded-tr-sm'
                          }`}
                        >
                          {isInbound && (
                            <span className="block text-xs font-medium text-green-600 mb-0.5">
                              {leadName}
                            </span>
                          )}
                          <p className="whitespace-pre-wrap break-words leading-relaxed">
                            {msg.content || <em className="text-muted-foreground text-xs">Template inviato</em>}
                          </p>
                          <span className="text-xs mt-1 block text-right text-muted-foreground">
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* PROMPT CANVAS */}
        <div className="flex-shrink-0 border-t border-border bg-card rounded-b-xl">
          {!loading && windowOpen && (
            <div className="p-3">
              <div className="relative">
                {showEmojiPicker && (
                  <div className="absolute bottom-full left-0 z-50 mb-2">
                    <Picker
                      data={data}
                      onEmojiSelect={handleEmojiSelect}
                      theme="light"
                      locale="it"
                      previewPosition="none"
                      skinTonePosition="none"
                    />
                  </div>
                )}
                <div className="flex items-end gap-2 bg-muted border border-border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-inset focus-within:ring-green-500">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(v => !v)}
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 mb-0.5"
                    title="Emoji"
                  >
                    <Smile className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTemplateShortcut(true)}
                    className="text-muted-foreground hover:text-green-600 transition-colors flex-shrink-0 mb-0.5"
                    title="Invia Template"
                  >
                    <FileText className="w-5 h-5" />
                  </button>
                  <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Scrivi un messaggio... (Invio per inviare, Shift+Invio per nuova riga)"
                    className="flex-1 border-0 bg-transparent resize-none p-0 text-sm focus-visible:ring-0 min-h-[36px] max-h-[100px]"
                    rows={1}
                  />
                  <Button
                    size="sm"
                    onClick={handleSendText}
                    disabled={!text.trim() || sending}
                    className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0 h-8 w-8 p-0 rounded-lg"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-1.5 text-center">
                <button
                  type="button"
                  onClick={() => setUseTemplateMode(true)}
                  className="text-xs text-muted-foreground hover:text-green-600 transition-colors"
                >
                  Usa un template →
                </button>
              </div>
            </div>
          )}

          {!loading && showTemplateZone && (
            <div className="p-3 space-y-3">
              <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-orange-700 leading-relaxed">
                  {conversation
                    ? 'Sei fuori dalla finestra di 24 ore. Per contattare questo lead devi usare un template approvato.'
                    : 'Nessuna conversazione con questo lead. Inizia inviando un template approvato.'}
                </p>
                {useTemplateMode && windowStatus?.window_active && (
                  <button
                    type="button"
                    onClick={() => setUseTemplateMode(false)}
                    className="ml-auto flex-shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              <select
                value={selectedSender}
                onChange={(e) => setSelectedSender(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Seleziona numero mittente WhatsApp</option>
                {senders.map((s) => (
                  <option key={s.id} value={s.phone_number || s.id}>
                    {s.friendly_name || s.phone_number}
                  </option>
                ))}
              </select>

              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Seleziona template approvato</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.friendly_name}
                  </option>
                ))}
              </select>

              {selectedTemplateObj ? (
                <div className="bg-muted border border-border rounded-lg px-3 py-2.5">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Anteprima</p>
                  <p className="text-sm text-foreground italic leading-relaxed whitespace-pre-wrap">
                    {selectedTemplateObj.body_text}
                  </p>
                </div>
              ) : (
                <div className="bg-muted border border-dashed border-border rounded-lg px-3 py-2.5">
                  <p className="text-xs text-muted-foreground italic text-center">
                    Seleziona un template per vedere l&apos;anteprima
                  </p>
                </div>
              )}

              <Button
                onClick={handleSendTemplate}
                disabled={!selectedSender || !selectedTemplate || sending}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-9 text-sm"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Invio in corso...' : 'Invia Template'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <WhatsAppTemplateSendModal
        open={showTemplateShortcut}
        onClose={() => setShowTemplateShortcut(false)}
        conversationId={conversation?.id}
        contactPhone={leadPhone}
        contactName={leadName}
        leadId={leadId}
        onSuccess={() => {
          if (conversation) {
            loadMessages(conversation.id);
          } else {
            init();
          }
        }}
      />
    </div>,
    document.body
  );
}
